/**
 * Day boundary resolution — the foundation for all date calculations.
 *
 * All functions take explicit parameters (no Date.now() calls) for determinism.
 * Timezone handling uses Intl.DateTimeFormat — no external date library needed.
 */

interface DateParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  weekday: number; // 0=Sun..6=Sat
}

/**
 * Extract local date/time parts for a given timestamp and timezone.
 * Uses Intl.DateTimeFormat for DST-safe timezone conversion.
 */
export function getLocalDateParts(timestamp: string, timezone: string): DateParts {
  const date = new Date(timestamp);
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    weekday: "short",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes): string => {
    const part = parts.find((p) => p.type === type);
    if (!part) throw new Error(`Missing date part: ${type}`);
    return part.value;
  };

  const weekdayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };

  // Intl.DateTimeFormat with hour12: false reports midnight as "24" in some engines
  const rawHour = parseInt(get("hour"), 10);

  return {
    year: parseInt(get("year"), 10),
    month: parseInt(get("month"), 10),
    day: parseInt(get("day"), 10),
    hour: rawHour === 24 ? 0 : rawHour,
    minute: parseInt(get("minute"), 10),
    weekday: weekdayMap[get("weekday")],
  };
}

/**
 * Get the effective date for a timestamp, respecting the day boundary hour.
 * If the local hour is before `boundaryHour`, the effective date is the previous day.
 *
 * Example: boundaryHour=4, timestamp at 2:30 AM local → effective date is yesterday.
 */
export function getEffectiveDate(
  timestamp: string,
  timezone: string,
  boundaryHour: number,
): string {
  const parts = getLocalDateParts(timestamp, timezone);

  if (parts.hour < boundaryHour) {
    return addDays(formatDate(parts.year, parts.month, parts.day), -1);
  }

  return formatDate(parts.year, parts.month, parts.day);
}

/**
 * Format year/month/day into YYYY-MM-DD string.
 */
export function formatDate(year: number, month: number, day: number): string {
  const y = String(year).padStart(4, "0");
  const m = String(month).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Parse a YYYY-MM-DD string into a Date at UTC noon (avoids DST edge issues).
 */
export function parseDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
}

/**
 * Add N days to a YYYY-MM-DD date string. Negative N subtracts days.
 */
export function addDays(dateStr: string, n: number): string {
  const d = parseDate(dateStr);
  d.setUTCDate(d.getUTCDate() + n);
  return formatDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

/**
 * Count the number of days between two YYYY-MM-DD dates.
 * Returns positive if `b` is after `a`, negative if before.
 */
export function daysBetween(a: string, b: string): number {
  const dateA = parseDate(a);
  const dateB = parseDate(b);
  const diffMs = dateB.getTime() - dateA.getTime();
  return Math.round(diffMs / (24 * 60 * 60 * 1000));
}

/**
 * Get the day of the week (0=Sun..6=Sat) for a YYYY-MM-DD date string.
 */
export function getDayOfWeek(dateStr: string): number {
  return parseDate(dateStr).getUTCDay();
}

/**
 * Get the day of the month (1-31) for a YYYY-MM-DD date string.
 */
export function getDayOfMonth(dateStr: string): number {
  return parseDate(dateStr).getUTCDate();
}

/**
 * Get the year and month from a YYYY-MM-DD date string.
 */
export function getYearMonth(dateStr: string): { year: number; month: number } {
  const d = parseDate(dateStr);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
}

/**
 * Get the last day of the month for a YYYY-MM-DD date string.
 */
export function getLastDayOfMonth(dateStr: string): string {
  const { year, month } = getYearMonth(dateStr);
  // Day 0 of next month = last day of this month
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return formatDate(year, month, lastDay);
}

/**
 * Get the first day of the month for a YYYY-MM-DD date string.
 */
export function getFirstDayOfMonth(dateStr: string): string {
  const { year, month } = getYearMonth(dateStr);
  return formatDate(year, month, 1);
}

/**
 * Compare two YYYY-MM-DD date strings.
 * Returns negative if a < b, 0 if equal, positive if a > b.
 */
export function compareDates(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}
